import React, { useEffect, useLayoutEffect } from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { navigate } from 'gatsby';
import {
  ViewBillDetails,
  AccountDetail,
  ViewBillMonthlyUsage,
  ViewBillAverageTemperature,
  QuickViewBillInfo,
  BillInfo,
  AutoPayInfo,
  PaymentPlanType,
  PgeEnergyTrackerData,
} from '../../__generated__/pge-types';
import Page2ColumnLayout from '../utility/page2-column-layout';
import PGEButton from '../buttons';
import { AccountParamsType } from '../../hooks/useViewBill';
import { useTranslation } from '../../hooks/useTranslation';
import Backdrop from '../../components/backdrop';
import ROUTES from '../../routes';
import Helmet from 'react-helmet';
import TEST_IDS from '../../constants/test_ids';
import { TpaAccountDetails } from '../need-more-time-to-pay/tpa/useTPA';
import AccountDropdown from '../account-dropdown';
import AccountViewBillDetails from './ViewBillDetails';
import { useLocation } from '@reach/router';
import AccountLeftNavBlock from '../utility/account-left-nav-block';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    centerColumn: {
      width: '100%',
    },
    noBills: {
      margin: theme.spacing(2, 0, 0, 0),
      padding: theme.spacing(2),
    },
  }),
);

type ViewBillProps = {
  accountParams: AccountParamsType | null | undefined;
  loading: boolean;
  billDetails: ViewBillDetails | null | undefined;
  selectedAccount: AccountDetail | null | undefined;
  monthUsage?: ViewBillMonthlyUsage | null | undefined;
  averageTemperature?: ViewBillAverageTemperature | null | undefined;
  quickSelectedAccount?: QuickViewBillInfo | null | undefined;
  latestPaymentHistory?: any;
  billInfo: BillInfo | null | undefined;
  autopay: AutoPayInfo | null | undefined;
  isPendingDisconnect: boolean | undefined;
  paymentPlanType: PaymentPlanType | null | undefined;
  encryptedAccountNumber: string | null | undefined;
  encryptedPersonId: string | undefined | null;
  tpaDetails?: TpaAccountDetails | undefined;
  pgeEnergyTracker?: PgeEnergyTrackerData | undefined | null;
  isSummaryBillAccount?: boolean | undefined;
  isNewAccount: boolean | null | undefined;
};

const ViewBill = ({
  accountParams,
  loading,
  billDetails,
  selectedAccount,
  monthUsage,
  averageTemperature,
  quickSelectedAccount,
  latestPaymentHistory,
  billInfo,
  autopay,
  isPendingDisconnect,
  paymentPlanType,
  encryptedAccountNumber,
  encryptedPersonId,
  pgeEnergyTracker,
  isSummaryBillAccount,
  tpaDetails,
  isNewAccount,
}: ViewBillProps) => {
  const energyTrackerPanelRef = React.useRef<HTMLDivElement>(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  useEffect(() => {
    location.hash !== '#energyUse' && window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    //Focusing the energy tracker widget with panel open.
    if (
      !loading &&
      location.hash === '#energyTracker' &&
      typeof window !== 'undefined'
    ) {
      if (energyTrackerPanelRef.current) {
        energyTrackerPanelRef?.current?.click();
        window.scrollTo({
          top: energyTrackerPanelRef?.current?.getBoundingClientRect().top,
          behavior: 'smooth',
        });
      }
    }
  }, [loading]);

  if (
    billDetails === undefined ||
    loading ||
    latestPaymentHistory === undefined
  ) {
    return <Backdrop forceOpen />;
  }

  return (
    <>
      <Helmet>
        <title>{t('VIEW_BILL_DETAILS')}</title>
      </Helmet>
      <Page2ColumnLayout leftColumn={<AccountLeftNavBlock />}>
        <Grid className={classes.centerColumn}>
          <Typography
            variant={'h1'}
            data-testid={TEST_IDS.VIEW_BILL_H1}
            style={{ marginBottom: '1rem' }}
          >
            {t('VIEW_BILL_DETAILS')}
          </Typography>
          <AccountDropdown />
          {!loading && !billInfo ? (
            <Paper className={classes.noBills} data-testid="no-bill-card">
              <Typography style={{ margin: '.5em 0' }}>
                {t('NO_BILL')}
              </Typography>
              <Typography>
                {`${t('ASSISTANCE')} ${t('CALL_FOR_ASSISTANCE_NUMBER')}`}
              </Typography>
              <Box display="flex" justifyContent="flex-end" width="100%">
                <PGEButton onClick={() => navigate(ROUTES.ACCOUNT)}>
                  {t('OK')}
                </PGEButton>
              </Box>
            </Paper>
          ) : (
            <AccountViewBillDetails
              isNewAccount={isNewAccount}
              accountParams={accountParams}
              loading={loading}
              billDetails={billDetails}
              selectedAccount={selectedAccount}
              billInfo={billInfo}
              autopay={autopay}
              isPendingDisconnect={isPendingDisconnect}
              paymentPlanType={paymentPlanType}
              encryptedAccountNumber={encryptedAccountNumber}
              encryptedPersonId={encryptedPersonId}
              monthUsage={monthUsage}
              averageTemperature={averageTemperature}
              quickSelectedAccount={quickSelectedAccount}
              latestPaymentHistory={latestPaymentHistory}
              tpaDetails={tpaDetails}
              pgeEnergyTracker={pgeEnergyTracker}
              isSummaryBillAccount={isSummaryBillAccount}
            />
          )}
        </Grid>
      </Page2ColumnLayout>
    </>
  );
};

export default ViewBill;
